/**
 * changeOrderLifecycle.ts (frontend)
 *
 * UI-side mirror of the change-order state machine in
 * `supabase/functions/server/changeOrderLifecycleCore.ts`: which transitions a
 * button may offer, and how statuses are labeled. The server re-validates
 * every transition; this is for rendering only. Keep the transition table and
 * labels identical to the server copy (parity-tested under Deno).
 */

export type CoStatus = 'draft' | 'pending_approval' | 'approved' | 'applied' | 'billed' | 'rejected' | 'cancelled';

export const CO_STATUSES: CoStatus[] = ['draft', 'pending_approval', 'approved', 'applied', 'billed', 'rejected', 'cancelled'];

export const CO_TRANSITIONS: Record<CoStatus, CoStatus[]> = {
  draft: ['pending_approval', 'cancelled'],
  pending_approval: ['approved', 'rejected', 'cancelled', 'draft'],
  approved: ['applied'],
  applied: ['billed'],
  billed: [],
  rejected: ['draft'],
  cancelled: [],
};

export const CO_STATUS_LABELS: Record<CoStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  applied: 'Applied',
  billed: 'Billed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const CO_TRANSITION_LABELS: Record<CoStatus, string> = {
  draft: 'Return to draft',
  pending_approval: 'Send for approval',
  approved: 'Record approval',
  applied: 'Apply to scope',
  billed: 'Record billing',
  rejected: 'Record rejection',
  cancelled: 'Cancel change order',
};

export function normalizeCoStatus(status: unknown): CoStatus {
  const s = String(status ?? 'draft').toLowerCase();
  if (s === 'pending') return 'pending_approval';
  if (s === 'implemented') return 'applied';
  return (CO_STATUSES as string[]).includes(s) ? (s as CoStatus) : 'draft';
}

export function allowedTransitions(from: CoStatus): CoStatus[] {
  return [...(CO_TRANSITIONS[from] ?? [])];
}

export function canEditContent(status: CoStatus): boolean {
  return status === 'draft' || status === 'rejected';
}

export function canToggleSuggestions(status: CoStatus): boolean {
  return status === 'draft' || status === 'rejected' || status === 'pending_approval' || status === 'approved';
}

export function isTerminal(status: CoStatus): boolean {
  return CO_TRANSITIONS[status].length === 0;
}

/** Tailwind classes for a status badge (outline variant). */
export function coStatusClasses(status: CoStatus): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-50 text-gray-700 border-gray-200';
    case 'pending_approval':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'approved':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'applied':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'billed':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'rejected':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'cancelled':
    default:
      return 'bg-slate-50 text-slate-500 border-slate-200';
  }
}

/** Which transitions get the primary (filled) button treatment. */
export function isPrimaryTransition(to: CoStatus): boolean {
  return to === 'pending_approval' || to === 'approved' || to === 'applied' || to === 'billed';
}
